import { EngraveSettings, TextAlignment, HorizontalAlignment, VerticalAlignment } from '../types';

/**
 * Googleフォーム連携サービス
 * C#版のGoogleFormServiceをTypeScriptに移植
 */
export class GoogleFormService {
  private readonly formBaseUrl = 'https://docs.google.com/forms/d/e/1FAIpQLSdG7c4AKr4aj8omfCIiGkg9NKBduHz9kAJVGYvZUjz-s-brTw/viewform';
  private readonly orderNoEntryId = 'entry.1059124980';
  private readonly contentEntryId = 'entry.957188824';
  private readonly backContentEntryId = 'entry.876995071';
  private readonly applyCountEntryId = 'entry.510163070';

  /**
   * Googleフォームの事前入力URLを生成
   * @param orderNo 注文番号
   * @param frontSettings 前面の彫刻設定
   * @param backSettings 後面の彫刻設定（オプション）
   * @returns 事前入力されたGoogleフォームのURL
   */
  generatePrefilledUrl(orderNo: string, frontSettings: EngraveSettings, backSettings?: EngraveSettings | null): string {
    if (!orderNo?.trim()) {
      throw new Error('注文番号を入力してください');
    }

    // 前面の処理
    const hasFrontContent = frontSettings &&
      ((frontSettings.text && frontSettings.text.trim().length > 0) || frontSettings.isImageMode);

    let frontContent: string;
    if (hasFrontContent) {
      // 前面に文字または画像がある場合：画像モードなら直接文字列、そうでなければJSON
      frontContent = frontSettings.isImageMode
        ? '画像をアップロードする'
        : this.serializeSettings(frontSettings);
    } else {
      // 前面が未入力の場合は「文字・画像入れは不要」を送信
      frontContent = '文字・画像入れは不要';
    }

    const params: Record<string, string> = {
      'usp': 'pp_url',
      [this.orderNoEntryId]: orderNo,
      [this.contentEntryId]: frontContent,
      [this.applyCountEntryId]: '全て'
    };

    // 後面の処理
    const hasBackContent = backSettings &&
      ((backSettings.text && backSettings.text.trim().length > 0) || backSettings.isImageMode);

    if (hasBackContent) {
      // 後面に文字または画像がある場合：画像モードなら直接文字列、そうでなければJSON
      const backContent = backSettings!.isImageMode
        ? '画像をアップロードする'
        : this.serializeSettings(backSettings!);
      params[this.backContentEntryId] = backContent;
    } else {
      // 後面が未入力の場合は「文字・画像入れは不要」を送信
      params[this.backContentEntryId] = '文字・画像入れは不要';
    }

    const queryParams = new URLSearchParams(params);
    return `${this.formBaseUrl}?${queryParams.toString()}`;
  }

  /**
   * 彫刻設定をJSON文字列にシリアライズ
   * @param settings 彫刻設定
   * @returns JSON文字列
   */
  private serializeSettings(settings: EngraveSettings): string {
    const data = {
      text: settings.text,
      fontFamily: settings.fontFamily,
      fontSize: settings.fontSize,
      alignment: TextAlignment[settings.alignment],
      isBold: settings.isBold,
      isItalic: settings.isItalic,
      isVertical: settings.isVertical,
      horizontalAlignment: HorizontalAlignment[settings.horizontalAlignment],
      verticalAlignment: VerticalAlignment[settings.verticalAlignment]
    };

    return JSON.stringify(data);
  }

  /**
   * JSON文字列から彫刻設定をデシリアライズ（受注側での復元用）
   * @param json JSON文字列
   * @returns 彫刻設定
   */
  deserializeSettings(json: string): EngraveSettings | null {
    if (!json?.trim()) {
      return null;
    }

    try {
      const dto = JSON.parse(json);
      if (!dto) {
        return null;
      }

      return {
        text: dto.text || '',
        fontFamily: dto.fontFamily || 'Arial',
        fontSize: dto.fontSize || 12,
        alignment: TextAlignment[dto.alignment as keyof typeof TextAlignment] ?? TextAlignment.Center,
        isBold: dto.isBold ?? false,
        isItalic: dto.isItalic ?? false,
        isVertical: dto.isVertical ?? false,
        horizontalAlignment: HorizontalAlignment[dto.horizontalAlignment as keyof typeof HorizontalAlignment] ?? HorizontalAlignment.Center,
        verticalAlignment: VerticalAlignment[dto.verticalAlignment as keyof typeof VerticalAlignment] ?? VerticalAlignment.Center
      };
    } catch (error) {
      console.error('JSON デシリアライズエラー:', error);
      return null;
    }
  }
}
